"use client";

import { useEffect, useState } from "react";
import { getTranslations } from "@/lib/i18n";
import { fetchJsonWithTimeout } from "@/lib/fetch-with-timeout";
import {
  KALENDER_TIMEOUT_MS,
  aankomstHref,
  aankomstLabel,
  hotelRegel,
  hotelVoorop,
  hotelsVanHotelblok,
  inbegrepenHotelId,
  kalenderAanvraag,
  voordeligsteAankomstdagen,
} from "@/lib/deal/voordeligste-aankomst";
import { haalDealHotels } from "@/lib/deal/deal-hotels-verzoek";

// Zelfde letter, grootte en grijs als de totaalregel onder de prijs
// (TOTAL_HINT_CLASS op de dealpagina).
const REGEL_KLASSE = "text-[12px] text-gray-400 mt-2 text-center leading-relaxed";

// Datumlink: een vaste, rustige onderstreping en iets donkerder grijs, zodat
// hij ook zonder hover (telefoon) als link herkenbaar is. `py-1` op het inline
// element vergroot het tikvlak zonder de regelhoogte of -breedte te veranderen.
const DATUMLINK_KLASSE = "py-1 underline decoration-gray-300 underline-offset-2 text-gray-500 hover:text-gray-700";

/**
 * Twee rustige tekstregels direct onder «Kies datum en boek» (30-10-2026):
 * de hotels van de deal en de voordeligste aankomstdagen. Geen blok, kader of
 * knoppen (Dennis 27-10 wees «Dit boek je» af). Alleen voor een gewone,
 * boekbare deal met één verblijf.
 *
 * De datumregel en (met `dealId`, zonder `hotels`) de hotelregel laden pas in
 * de browser, zodat de HTML voor iedereen gelijk blijft. De hotelregel volgt
 * het hotelblok: zelfde namen, zelfde volgorde, zelfde hotels. Zonder antwoord binnen KALENDER_TIMEOUT_MS, bij een fout of zonder
 * boekbare dag verschijnt hij niet; er staat nooit een laadtekst of lege ruimte.
 */
export default function BoekKnopHotelsDatums({
  deal,
  bookingHref,
  locale = "nl",
  hotels = null,
  dealId = null,
  hotelIds = null,
  hotelLeadId = null,
  hotelVolgorde = null,
  kerstHotels = null,
  isIngVoucher = false,
  isCatalogVoucher = false,
  isRoundtrip = false,
  isFarTravel = false,
}) {
  const uit = Boolean(isIngVoucher || isCatalogVoucher || isRoundtrip || isFarTravel || !deal || !bookingHref);
  const [dagen, setDagen] = useState(null);
  const [gehaald, setGehaald] = useState(null);
  const zelfHalen = !uit && hotels == null && dealId != null;

  // Zonder hotels-prop: dezelfde hotels als het hotelblok, uit het gedeelde
  // verzoek (samen met DealPartnerHotels één `/api/hotels/details`).
  useEffect(() => {
    if (!zelfHalen) return undefined;
    let actief = true;
    haalDealHotels(dealId).then((lijst) => {
      if (actief) setGehaald(lijst);
    });
    return () => {
      actief = false;
    };
  }, [zelfHalen, dealId]);

  useEffect(() => {
    if (uit) return undefined;
    const aanvraag = kalenderAanvraag(deal);
    // Een boek-URL met een ander gezelschap of een andere duur: geen regel.
    if (!aanvraag || !aankomstHref(bookingHref, aanvraag.range.startDate, aanvraag.los)) return undefined;
    const afbreken = new AbortController();
    fetchJsonWithTimeout(
      "/api/booking/calendar",
      {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(aanvraag.body),
      },
      afbreken.signal,
      KALENDER_TIMEOUT_MS,
    )
      .then(({ res, data }) => {
        if (afbreken.signal.aborted || !res?.ok) return;
        const gevonden = voordeligsteAankomstdagen(data, aanvraag);
        if (gevonden.length > 0) {
          setDagen({ los: aanvraag.los, data: gevonden, hotelId: inbegrepenHotelId(data, aanvraag, gevonden) });
        }
      })
      .catch(() => {});
    return () => afbreken.abort();
  }, [uit, deal, bookingHref]);

  if (uit) return null;
  const { t } = getTranslations(locale);
  const hotelTekst = hotelRegel(
    hotels != null
      ? hotelVoorop(hotels, dagen?.hotelId)
      : zelfHalen && gehaald
        ? hotelsVanHotelblok(gehaald, {
            hotelIds: hotelIds || [],
            leadHotelId: hotelLeadId,
            volgorde: hotelVolgorde,
            kerstHotels,
            voorop: dagen?.hotelId,
          })
        : null,
    t,
    locale,
  );
  const links = (dagen?.data || [])
    .map((date) => ({ date, href: aankomstHref(bookingHref, date, dagen.los) }))
    .filter((l) => l.href);
  if (!hotelTekst && links.length === 0) return null;

  return (
    <>
      {hotelTekst && (
        <p data-testid="boekknop-hotels" className={REGEL_KLASSE}>
          {hotelTekst}
        </p>
      )}
      {links.length > 0 && (
        <p data-testid="boekknop-aankomst" className={REGEL_KLASSE}>
          {t("deal.boekregel.voordeligste_aankomst")}{" "}
          {links.map((l, i) => (
            <span key={l.date}>
              {i > 0 && ", "}
              <a href={l.href} className={DATUMLINK_KLASSE}>
                {aankomstLabel(l.date, locale)}
              </a>
            </span>
          ))}
        </p>
      )}
    </>
  );
}
